import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate } from '../types/hr.types';
import { useToast } from './ui/Toast';
import { FileText, Send, DollarSign, Calendar } from 'lucide-react';
interface OfferLetterModalProps {
  candidate: Candidate | null;
  isOpen: boolean;
  onClose: () => void;
  onSend: (candidate: Candidate, payload: {
    offerAmount: number;
  }) => void;
}
export function OfferLetterModal({
  candidate,
  isOpen,
  onClose,
  onSend
}: OfferLetterModalProps) {
  const {
    addToast
  } = useToast();
  const [offerAmount, setOfferAmount] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [letterBody, setLetterBody] = useState('');
  useEffect(() => {
    if (isOpen && candidate) {
      const defaultAmount = candidate.offerAmount ? candidate.offerAmount.toString() : '65000';
      setOfferAmount(defaultAmount);
      const defaultStart = candidate.desiredStartDate || (() => {
        const d = new Date();
        d.setDate(d.getDate() + 14);
        return d.toISOString().split('T')[0];
      })();
      setStartDate(defaultStart);
      const template = `Dear ${candidate.firstName},

We are thrilled to offer you the position of ${candidate.role} in our ${candidate.department} department. 

Your starting salary will be $${Number(defaultAmount).toLocaleString()} per year. We would like your first day to be ${new Date(defaultStart).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}.

This offer is contingent upon the successful completion of a background check. Please review the attached benefits summary and employee handbook.

We look forward to welcoming you to the team!

Sincerely,
${candidate.recruiter || 'Human Resources'}
`;
      setLetterBody(template);
    }
  }, [isOpen, candidate]);
  // Update letter body when amount or date changes, but only if the user hasn't heavily edited it
  // For simplicity, we'll just let them edit the text directly and not auto-sync after initial load,
  // or we can provide a "Regenerate" button. Let's just keep it simple: the fields above are for structured data,
  // the text area is the final source of truth for the document.
  if (!candidate) return null;
  const handleSend = () => {
    const amount = parseInt(offerAmount.replace(/[^0-9]/g, ''), 10) || 0;
    onSend(candidate, {
      offerAmount: amount
    });
    addToast({
      type: 'success',
      title: 'Offer Sent via DocuSign',
      message: `Offer letter sent to ${candidate.email}`
    });
    onClose();
  };
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white';
  return <Modal isOpen={isOpen} onClose={onClose} title="Review & Send Offer" maxWidth="3xl" data-id="element-980">
      <div className="flex flex-col md:flex-row gap-6 h-[70vh]" data-id="element-981">
        {/* Left Col: Settings */}
        <div className="w-full md:w-64 flex-shrink-0 space-y-4" data-id="element-982">
          <div className="bg-blue-50 p-4 rounded-xl border border-blue-100" data-id="element-983">
            <h3 className="font-semibold text-blue-900 text-sm mb-1" data-id="element-984">
              Offer Details
            </h3>
            <p className="text-xs text-blue-700 mb-4" data-id="element-985">
              Set the core terms to update the candidate record.
            </p>

            <div className="space-y-4" data-id="element-986">
              <div data-id="element-987">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-988">
                  Annual Salary
                </label>
                <div className="relative" data-id="element-989">
                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-990" />
                  <input type="text" value={offerAmount} onChange={e => setOfferAmount(e.target.value)} className={`${inputClass} pl-8`} data-id="element-991" />
                </div>
              </div>

              <div data-id="element-992">
                <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-993">
                  Proposed Start Date
                </label>
                <div className="relative" data-id="element-994">
                  <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-995" />
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={`${inputClass} pl-8`} data-id="element-996" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-200" data-id="element-997">
            <h3 className="font-semibold text-gray-900 text-sm mb-2" data-id="element-998">
              Delivery Method
            </h3>
            <div className="flex items-center gap-2 text-sm text-gray-700 bg-white p-2 rounded border border-gray-200" data-id="element-999">
              <FileText className="h-4 w-4 text-blue-600" data-id="element-1000" />
              DocuSign Integration
            </div>
            <p className="text-[11px] text-gray-500 mt-2 leading-tight" data-id="element-1001">
              The letter will be generated as a PDF and routed through DocuSign
              for signature.
            </p>
          </div>
        </div>

        {/* Right Col: Document Preview */}
        <div className="flex-1 flex flex-col min-w-0 border border-gray-200 rounded-xl overflow-hidden bg-gray-50" data-id="element-1002">
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-center justify-between" data-id="element-1003">
            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1004">
              Letter Preview
            </span>
            <span className="text-[10px] text-gray-400" data-id="element-1005">Editable</span>
          </div>
          <textarea value={letterBody} onChange={e => setLetterBody(e.target.value)} className="flex-1 w-full p-6 resize-none focus:outline-none focus:ring-inset focus:ring-2 focus:ring-blue-500 text-sm text-gray-800 font-serif leading-relaxed" spellCheck={false} data-id="element-1006" />
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100" data-id="element-1007">
        <Button variant="ghost" onClick={onClose} className="text-gray-600 border border-gray-200 hover:bg-gray-50" data-id="element-1008">
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSend} className="bg-blue-600 hover:bg-blue-700" leftIcon={<Send className="h-4 w-4" data-id="element-1010" />} data-id="element-1009">
          Send via DocuSign
        </Button>
      </div>
    </Modal>;
}